<template>
  <div
    class="accruals-condition-row"
    :class="{ 'accruals-condition-row--editing': editing }"
    :data-testid="dataTestid"
  >
    <span v-if="showIndex" class="accruals-condition-row__index">{{ index + 1 }}</span>

    <template v-if="editing">
      <v-select
        :value="condition.field"
        :items="fieldItems"
        item-text="label"
        item-value="value"
        dense
        outlined
        hide-details="auto"
        placeholder="Select field"
        :error="Boolean(fieldError)"
        :menu-props="fieldMenuProps"
        class="accruals-condition-row__field accruals-condition-row__control"
        :data-testid="`accruals-configuration-page__condition-field--${index}`"
        @input="update('field', $event)"
      >
        <template #selection="{ item }">
          {{ getFieldLabel(item.value || item) }}
        </template>
        <template #item="{ item }">
          <span class="accruals-condition-row__menu-label">{{ getFieldLabel(item.value || item) }}</span>
        </template>
      </v-select>

      <v-select
        :value="condition.operator"
        :items="operatorItems"
        item-text="label"
        item-value="value"
        dense
        outlined
        hide-details="auto"
        placeholder="Select operator"
        :error="Boolean(operatorError)"
        :menu-props="operatorMenuProps"
        class="accruals-condition-row__operator accruals-condition-row__control"
        :data-testid="`accruals-configuration-page__condition-operator--${index}`"
        @input="update('operator', $event)"
      >
        <template #selection="{ item }">
          {{ getOperatorMenuLabel(item) }}
        </template>
        <template #item="{ item }">
          <span class="accruals-condition-row__menu-label">{{ getOperatorMenuLabel(item) }}</span>
        </template>
      </v-select>

      <lb-date
        v-if="fieldType === 'date'"
        :value="displayValue"
        hidedetails
        label=""
        menu
        clearable
        :disabled="!needsValue"
        :placeholder="valuePlaceholder"
        :error="valueError"
        class="accruals-condition-row__value accruals-condition-row__control"
        :data-testid="`accruals-configuration-page__condition-value--${index}`"
        @input="update('value', $event)"
      />

      <v-text-field
        v-else
        :value="displayValue"
        :type="valueInputType"
        :disabled="!needsValue"
        :placeholder="valuePlaceholder"
        :error-messages="valueError ? [valueError] : []"
        hide-details="auto"
        dense
        outlined
        class="accruals-condition-row__value accruals-condition-row__control"
        :data-testid="`accruals-configuration-page__condition-value--${index}`"
        @input="update('value', $event)"
      />

      <div
        v-if="inlineEdit && editing && !showDelete"
        class="accruals-condition-row__delete-slot"
        aria-hidden="true"
      />
      <div
        v-else-if="(inlineEdit && editing && showDelete) || (!inlineEdit && (rowActions || editing))"
        class="accruals-condition-row__actions"
      >
        <base-button
          v-if="rowActions && !inlineEdit"
          icon-style
          small
          class="accruals-condition-row__action-btn accruals-condition-row__action-btn--confirm"
          :image="assets.ICON_CHECK"
          image-alt="Confirm condition"
          :disabled="saving"
          aria-label="Confirm condition"
          :data-testid="`accruals-configuration-page__condition-confirm--${index}`"
          @click="$emit('confirm')"
        />
        <base-button
          v-if="showDelete"
          icon-style
          small
          class="accruals-condition-row__delete-btn"
          :image="inlineEdit ? assets.ICON_DELETE : assets.ICON_X_CIRCLE"
          image-alt="Delete condition"
          :disabled="saving"
          aria-label="Delete condition"
          :data-testid="`accruals-configuration-page__condition-delete--${index}`"
          @click="$emit('delete')"
        />
      </div>    
    </template>

    <template v-else>
      <div class="accruals-condition-row__pill accruals-condition-row__pill--field">
        {{ fieldDisplayLabel }}
      </div>
      <div class="accruals-condition-row__pill accruals-condition-row__pill--operator">
        {{ operatorDisplayLabel }}
      </div>
      <div class="accruals-condition-row__pill accruals-condition-row__pill--value">
        {{ valueDisplayLabel }}
      </div>
      <div v-if="rowActions" class="accruals-condition-row__actions">
        <base-button
          icon-style
          small
          class="accruals-condition-row__action-btn accruals-condition-row__action-btn--edit"
          :image="assets.ICON_EDIT"
          image-alt="Edit condition"
          :disabled="saving"
          aria-label="Edit condition"
          :data-testid="`accruals-configuration-page__condition-edit--${index}`"
          @click="$emit('edit')"
        />
        <base-button
          icon-style
          small
          class="accruals-condition-row__delete-btn"
          :image="assets.ICON_X_CIRCLE"
          image-alt="Delete condition"
          :disabled="saving"
          aria-label="Delete condition"
          :data-testid="`accruals-configuration-page__condition-delete--${index}`"
          @click="$emit('delete')"
        />
      </div>    
    </template>
  </div>
</template>

<script>
import {
  ACCRUALS_CONFIG_ASSETS,
  CONDITION_OPERATORS,
  getOperatorsForField,
} from '../accrualsConfigurationConstants';
import {
  formatConditionValue,
  getFieldMeta,
  isEmptyValueOperator,
} from '../accrualsConditionValidation';
import {
  formatConditionDisplayValue,
  formatFieldDisplayLabel,
  getOperatorDisplayLabel,
  getValuePlaceholder,
} from '../accrualsRulesBuilderUtils';

const EMPTY_ERRORS = { field: '', operator: '', value: '' };
const SELECT_MENU_MAX_HEIGHT = 280;

export default {
  name: 'AccrualsConditionRow',
  props: {
    condition: { type: Object, required: true },
    index: { type: Number, required: true },
    fieldItems: { type: Array, default: () => [] },
    editing: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    rowActions: { type: Boolean, default: false },
    inlineEdit: { type: Boolean, default: false },
    showDelete: { type: Boolean, default: true },
    showIndex: { type: Boolean, default: true },
    errors: { type: Object, default: () => ({ ...EMPTY_ERRORS }) },
    dataTestid: { type: String, default: 'accruals-condition-row' },
  },
  data() {
    return {
      assets: ACCRUALS_CONFIG_ASSETS,
    };
  },
  computed: {
    fieldMeta() {
      return getFieldMeta(this.condition.field);
    },
    fieldType() {
      return this.fieldMeta?.type || 'text';
    },
    operatorItems() {
      if (!this.condition.field) return CONDITION_OPERATORS;
      return getOperatorsForField(this.condition.field);
    },
    needsValue() {
      return !isEmptyValueOperator(this.condition.operator || this.condition.op);
    },
    valueInputType() {
      if (!this.needsValue) return 'text';
      if (this.fieldType === 'number') return 'text';
      if (this.fieldType === 'date') return 'date';
      return 'text';
    },
    valuePlaceholder() {
      if (!this.needsValue) return '—';
      return getValuePlaceholder(this.fieldType);
    },
    displayValue() {
      if (!this.needsValue) return '';
      return formatConditionValue(this.fieldType, this.condition.value);
    },
    fieldDisplayLabel() {
      return formatFieldDisplayLabel(this.condition.field) || '—';
    },
    operatorDisplayLabel() {
      return this.getOperatorMenuLabel({ value: this.condition.operator || this.condition.op }) || '—';
    },
    valueDisplayLabel() {
      if (!this.needsValue) return '—';
      return formatConditionDisplayValue(this.fieldType, this.condition.value);
    },
    fieldError() {
      return this.errors?.field || '';
    },
    operatorError() {
      return this.errors?.operator || '';
    },
    valueError() {
      return this.errors?.value || '';
    },
    fieldMenuProps() {
      return {
        contentClass: 'accruals-condition-row__menu accruals-condition-row__menu--field',
        offsetY: true,
        nudgeBottom: 4,
        maxHeight: SELECT_MENU_MAX_HEIGHT,
        auto: true,
      };
    },
    operatorMenuProps() {
      return {
        contentClass: 'accruals-condition-row__menu accruals-condition-row__menu--operator',
        offsetY: true,
        nudgeBottom: 4,
        maxWidth: 206,
        minWidth: 206,
        maxHeight: SELECT_MENU_MAX_HEIGHT,
        auto: true,
      };
    },
  },
  methods: {
    getFieldLabel(value) {
      return formatFieldDisplayLabel(value);
    },
    getOperatorMenuLabel(item) {
      const value = item?.value ?? item;
      const match = this.operatorItems.find((operator) => operator.value === value);
      if (match?.label) return match.label;
      return getOperatorDisplayLabel(value, this.fieldType);
    },
    update(key, value) {
      if (!this.editing) return;
      const next = { ...this.condition, [key]: value };
      if (key === 'field') {
        const allowed = getOperatorsForField(value).map((item) => item.value);
        if (!allowed.includes(next.operator)) {
          next.operator = allowed[0] || 'eq';
        }
        const nextType = getFieldMeta(value)?.type;
        const prevType = this.fieldType;
        if (nextType !== prevType) {
          next.value = '';
        }
      }
      if (key === 'operator' && isEmptyValueOperator(value)) {
        next.value = '';
      }
      this.$emit('update', next);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsConditionRow.scss';
</style>

<style lang="scss">
@import './AccrualsConditionRowMenu.scss';
</style>
